export type AuthenticationMode = "required" | "disabled";

export interface AppConfig {
  readonly apiOrigin: string;
  readonly authenticationMode: AuthenticationMode;
}

export interface ConfigurationEnvironment {
  readonly VITE_GROUNDSPARK_AUTHENTICATION?: string;
  readonly VITE_GROUNDSPARK_SERVER?: string;
}

export function readBrowserConfig(
  environment: ConfigurationEnvironment,
  currentLocation: Location,
): AppConfig {
  const authenticationMode = environment.VITE_GROUNDSPARK_AUTHENTICATION ?? "required";
  if (authenticationMode !== "required" && authenticationMode !== "disabled") {
    throw new Error("VITE_GROUNDSPARK_AUTHENTICATION must be required or disabled.");
  }

  const apiOrigin = canonicalHttpOrigin(
    environment.VITE_GROUNDSPARK_SERVER ?? "http://127.0.0.1:3000",
    "VITE_GROUNDSPARK_SERVER",
  );
  const appOrigin = canonicalHttpOrigin(currentLocation.origin, "application origin");
  if (
    authenticationMode === "required" &&
    (!secureOrLoopback(appOrigin) || !secureOrLoopback(apiOrigin))
  ) {
    throw new Error("Password authentication requires HTTPS or a loopback origin.");
  }

  return {
    apiOrigin,
    authenticationMode,
  };
}

function canonicalHttpOrigin(value: string, name: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${name} must be an absolute HTTP(S) origin.`);
  }
  if (
    (url.protocol !== "http:" && url.protocol !== "https:") ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new Error(`${name} must be an absolute HTTP(S) origin.`);
  }
  return url.origin;
}

function secureOrLoopback(origin: string): boolean {
  const url = new URL(origin);
  return url.protocol === "https:" || isLoopbackHost(url.hostname);
}

function isLoopbackHost(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "[::1]") return true;
  const octets = hostname.split(".");
  return (
    octets.length === 4 &&
    octets[0] === "127" &&
    octets.every((octet) => {
      const value = Number(octet);
      return Number.isInteger(value) && value >= 0 && value <= 255;
    })
  );
}
